import React, { useEffect, useMemo, useRef, useState } from "react";
import { StyleSheet, Text, View, Animated } from "react-native";
import CustomText from "../../components/common/general/CustomText/CustomText";
import { colors, radii, spacing } from "../../styles/design-system";
import CustomButton from "../../components/common/general/CustomButton/CustomButton";
import { CaretLeft, CaretRight } from "@phosphor-icons/react";
import { Circle, CircleCheck } from "lucide-react";
import { FormattedObjective } from "../../graphql/generated/graphql";
import useResponsiveScreen from "../../hooks/useResponsiveScreen";

export type Objective = Pick<FormattedObjective, "id" | "objective" | "status" | "evalCriteria"> & {
  isCompleted?: boolean;
};

interface LearningGoalsContainerProps {
  objectives: Objective[];
}

const EXPANDED_WIDTH = 280;
const COLLAPSED_WIDTH = 56;

const LearningGoalsContainer: React.FC<LearningGoalsContainerProps> = ({ objectives }) => {
  const { isLarge, isXXLarge } = useResponsiveScreen();

  const [menuVisible, setMenuVisible] = useState(isXXLarge);
  const [selectedObjectiveId, setSelectedObjectiveId] = useState<string | null>(null);

  const defaultActiveObjectiveId = useMemo(() => {
    if (!objectives.length) {
      return null;
    }

    // Active objective:
    // - first objective that is NOT completed
    // - if all completed, fall back to the last objective
    const firstIncomplete = objectives.find((objective) => objective.status !== "completed");

    const activeObjective = firstIncomplete ?? objectives[objectives.length - 1];

    return activeObjective?.id ?? null;
  }, [objectives]);

  useEffect(() => {
    if (selectedObjectiveId !== defaultActiveObjectiveId) {
      setSelectedObjectiveId(defaultActiveObjectiveId);
    }
  }, [defaultActiveObjectiveId, selectedObjectiveId]);

  const animatedWidth = useRef(new Animated.Value(isXXLarge ? EXPANDED_WIDTH : COLLAPSED_WIDTH)).current; // Start at the default position

  const completeObjectivesCount = objectives.filter((objective) => objective.status === "completed").length;

  const handleExpand = () => {
    if (menuVisible) {
      setMenuVisible(false);
      Animated.timing(animatedWidth, {
        toValue: COLLAPSED_WIDTH,
        duration: 300,
        useNativeDriver: false,
      }).start();
    } else {
      setMenuVisible(true);
      Animated.timing(animatedWidth, {
        toValue: EXPANDED_WIDTH,
        duration: 300,
        useNativeDriver: false,
      }).start();
    }
  };

  return (
    <Animated.View
      style={[styles.learningGoalsContainer, isLarge && styles.learningGoalsContainerLarge, { width: animatedWidth }]}>
      {/* Header */}
      {menuVisible ? (
        <View style={styles.header}>
          <CustomText useTranslationText={false} text="Learning Goals" style={styles.title} weight="bold" size="s" />
          <CustomText
            text="Goals Count"
            interpolation={{ completedCount: completeObjectivesCount, total: objectives.length }}
            style={styles.subTitle}
          />

          <View style={styles.minimizeButtonContainer}>
            <CustomButton
              leftIcon={<CaretLeft size={16} />}
              text=""
              styleType="secondaryLightened"
              style={styles.minimizeButton}
              onPress={handleExpand}
            />
          </View>
        </View>
      ) : (
        <View style={styles.maximizeButtonContainer}>
          <CustomButton
            leftIcon={<CaretRight size={16} />}
            text=""
            styleType="secondaryLightened"
            onPress={handleExpand}
          />
        </View>
      )}

      {/* Content */}
      <View style={menuVisible ? styles.contentExpanded : styles.contentCollapsed}>
        {objectives.map((objective) => {
          const isSelected = selectedObjectiveId === objective.id;
          const objectiveCriteria = (objective.evalCriteria ?? []).filter((criteria): criteria is string =>
            Boolean(criteria)
          );
          return (
            <View
              style={[
                styles.objectiveContainer,
                menuVisible ? styles.objectiveContainerExpanded : styles.objectiveContainerCollapsed,
                menuVisible && isSelected && styles.objectiveContainerActive,
              ]}
              key={objective.id ?? objective.objective}>
              <View style={[styles.objectiveIcon, menuVisible && styles.objectiveIconFull]}>
                {objective.status === "completed" ? (
                  <CircleCheck size={20} color={colors.success[600]} />
                ) : (
                  <View
                    style={[
                      styles.circleHighlight,
                      isSelected && styles.circleHighlightActive,
                      isSelected && !menuVisible && styles.circleHighlightCollapsedActive,
                    ]}>
                    <Circle
                      size={20}
                      color={menuVisible ? colors.grey[400] : colors.grey[300]}
                      strokeWidth={isSelected && !menuVisible ? 2.5 : 2}
                    />
                  </View>
                )}
              </View>
              {menuVisible ? (
                <View style={styles.objectiveContent}>
                  <Text
                    style={[styles.objectiveText, objective.status === "completed" && styles.completedObjectiveText]}>
                    {objective.objective}
                  </Text>
                  {isSelected && objectiveCriteria.length > 0 ? (
                    <View style={styles.criteriaList}>
                      {objectiveCriteria.map((criteria, index) => (
                        <View
                          style={styles.criteriaItem}
                          key={`${objective.id ?? objective.objective}-criteria-${index}`}>
                          <View style={styles.criteriaDot} />
                          <Text style={styles.criteriaText}>{criteria}</Text>
                        </View>
                      ))}
                    </View>
                  ) : null}
                </View>
              ) : null}
            </View>
          );
        })}
        {!menuVisible ? (
          <CustomText
            text={`${completeObjectivesCount}/${objectives.length}`}
            style={styles.counterText}
            useTranslationText={false}
          />
        ) : null}
      </View>
    </Animated.View>
  );
};

const styles = StyleSheet.create({
  learningGoalsContainer: {
    borderWidth: 1,
    borderColor: colors.grey[200],
    overflow: "hidden",
    borderRadius: radii.xl,
    backgroundColor: "#fff",
    shadowColor: "rgba(0, 0, 0, 0.08)",
    shadowOffset: { width: 0, height: 8 },
    shadowOpacity: 1,
    shadowRadius: 16,
    elevation: 4,
    position: "absolute",
    top: 8,
    left: 8,
    zIndex: 3,
    // @ts-expect-error - works for web
    height: "fit-content",
  },
  learningGoalsContainerLarge: {
    top: spacing.sm,
    left: spacing.sm,
  },
  header: {
    position: "relative",
    paddingTop: spacing.sm,
    paddingHorizontal: spacing.md,
    paddingBottom: 12,
    borderBottomWidth: 1,
    borderBottomColor: colors.grey[200],
  },
  title: {
    lineHeight: 21,
    color: colors.grey[900],
  },
  subTitle: {
    fontSize: 10,
    lineHeight: 15,
    color: colors.grey[600],
  },
  minimizeButtonContainer: {
    position: "absolute",
    top: spacing.md,
    right: spacing.md,
    zIndex: 1,
    alignItems: "center",
    justifyContent: "center",
  },
  minimizeButton: {
    padding: 6,
  },
  maximizeButtonContainer: {
    alignItems: "center",
    justifyContent: "center",
    paddingVertical: spacing.xxs,
    borderBottomWidth: 1,
    borderBottomColor: colors.grey[200],
  },
  contentCollapsed: {
    paddingTop: spacing.sm,
    paddingBottom: 12,
    gap: 12,
    alignItems: "center",
  },
  contentExpanded: {
    padding: spacing.md,
    gap: spacing.xs,
  },
  objectiveContainer: {
    flexDirection: "row",
    gap: spacing.xs,
    borderRadius: 16,
  },
  objectiveContainerExpanded: {
    alignItems: "flex-start",
    padding: 6,
  },
  objectiveContainerCollapsed: {
    width: "100%",
    justifyContent: "center",
  },
  objectiveContainerActive: {
    backgroundColor: colors.brand[50],
  },
  objectiveIcon: {
    borderRadius: "50%",
    alignItems: "center",
    justifyContent: "center",
  },
  objectiveIconFull: {
    marginTop: 4,
  },
  circleHighlight: {
    width: 24,
    height: 24,
    borderRadius: 12,
    alignItems: "center",
    justifyContent: "center",
    borderWidth: 2,
    borderColor: "transparent",
    backgroundColor: colors.white,
  },
  circleHighlightActive: {
    borderColor: colors.brand[200],
  },
  circleHighlightCollapsedActive: {
    borderWidth: 3,
  },
  objectiveText: {
    fontSize: 12,
    fontWeight: 500,
    lineHeight: 20,
    color: colors.grey[900],
  },
  objectiveContent: {
    flex: 1,
  },
  criteriaList: {
    gap: 6,
    marginTop: 4,
    paddingRight: spacing.xs,
  },
  criteriaItem: {
    flexDirection: "row",
    alignItems: "flex-start",
    gap: 6,
  },
  criteriaDot: {
    width: 4,
    height: 4,
    borderRadius: 2,
    marginTop: 8,
    backgroundColor: colors.brand[500],
  },
  criteriaText: {
    flex: 1,
    fontSize: 11,
    lineHeight: 16,
    color: colors.grey[700],
  },
  completedObjectiveText: {
    color: colors.grey[500],
    opacity: 0.7,
    textDecorationLine: "line-through",
  },
  counterText: {
    fontSize: 12,
    fontWeight: 600,
    lineHeight: 18,
    color: colors.grey[900],
  },
});

export default LearningGoalsContainer;
